<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisSelect from 'dashboard/components-next/LaisSelect.vue';
import LaisToggle from 'dashboard/components-next/LaisToggle.vue';
import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_PURPOSES,
  PROFILE_OPTIONS,
  input,
} from '../utils/minhaLaisOptions';
import { consentEnabled as consentEnabledFor } from '../utils/minhaLaisPresenters';

const props = defineProps({
  notificationContactId: { type: [Number, String], default: null },
  notificationContactOptions: { type: Array, default: () => [] },
  selectedNotificationContact: { type: Object, default: null },
  notificationProfile: { type: String, default: 'TENANT' },
  consents: { type: Array, default: () => [] },
  isSavingConsents: { type: Boolean, default: false },
});

const emit = defineEmits([
  'searchInput',
  'contactChange',
  'profileSelect',
  'consentChange',
  'applyAllProfiles',
  'tooltipClick',
]);

const notificationSearch = defineModel('notificationSearch', {
  type: String,
  default: '',
});

const consentEnabled = (purpose, channel) =>
  consentEnabledFor(
    props.consents,
    purpose,
    channel,
    props.notificationProfile
  );
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Preferências de notificações">
    <div class="flex items-start justify-between gap-3 mb-4">
      <p class="text-[13px] text-lais-text-muted max-w-[720px]">
        Escolha quais comunicações o cliente quer receber da Imudei por canal e
        tipo de produto.
      </p>
      <button
        type="button"
        class="inline-flex items-center gap-2 rounded-full bg-[#f5eef9] px-3 py-1.5 text-[12px] font-semibold text-lais-primary"
        @click="emit('tooltipClick')"
      >
        <span class="i-lucide-info size-3.5" aria-hidden="true" />
        O que é isso?
      </button>
    </div>

    <div class="grid gap-4 lg:grid-cols-[minmax(0,280px)_1fr]">
      <div class="grid gap-3">
        <LaisField label="Buscar cliente">
          <input
            v-model="notificationSearch"
            type="text"
            :class="input"
            placeholder="Nome, e-mail ou telefone"
            @input="emit('searchInput')"
          />
        </LaisField>

        <LaisField label="Cliente selecionado">
          <LaisSelect
            :model-value="notificationContactId"
            :options="notificationContactOptions"
            placeholder="Escolha um cliente"
            searchable
            @update:model-value="value => emit('contactChange', value)"
          />
        </LaisField>

        <div
          v-if="selectedNotificationContact"
          class="rounded-lais border border-[#e5e7eb] bg-[#faf8fd] p-4"
        >
          <p class="text-[13px] font-semibold text-lais-text">
            {{ selectedNotificationContact.name }}
          </p>
          <p class="mt-1 text-[12px] text-[#6b7280]">
            {{
              selectedNotificationContact.email ||
              selectedNotificationContact.phone_number ||
              'Sem e-mail/telefone'
            }}
          </p>
        </div>
      </div>

      <div class="grid gap-4">
        <div class="flex flex-wrap gap-2">
          <button
            v-for="profile in PROFILE_OPTIONS"
            :key="profile.value"
            type="button"
            class="inline-flex h-10 items-center rounded-full px-4 text-[13px] font-semibold transition-colors"
            :class="
              notificationProfile === profile.value
                ? 'bg-lais-primary text-white'
                : 'bg-white border border-[#e5e7eb] text-lais-text-body hover:border-lais-primary/40'
            "
            @click="emit('profileSelect', profile.value)"
          >
            {{ profile.label }}
          </button>
        </div>

        <div
          v-if="!notificationContactId"
          class="rounded-lais border border-dashed border-[#d8d5de] bg-[#fcfbfd] px-4 py-8 text-center text-[13px] text-[#8a8793]"
        >
          Escolha um cliente para editar os consentimentos.
        </div>

        <div v-else class="grid gap-4">
          <article
            v-for="purpose in NOTIFICATION_PURPOSES"
            :key="purpose.value"
            class="rounded-lais border border-[#e5e7eb] bg-white p-4"
          >
            <div
              class="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
            >
              <div>
                <h3 class="text-[15px] font-semibold text-lais-text">
                  {{ purpose.label }}
                </h3>
                <p class="mt-1 text-[12px] leading-relaxed text-[#6b7280]">
                  {{ purpose.helper }}
                </p>
              </div>
            </div>

            <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div
                v-for="channel in NOTIFICATION_CHANNELS"
                :key="`${purpose.value}-${channel.value}`"
              >
                <LaisToggle
                  :model-value="consentEnabled(purpose.value, channel.value)"
                  :disabled="isSavingConsents"
                  :label="channel.label"
                  :helper="`Perfil atual: ${notificationProfile}`"
                  @update:model-value="
                    value =>
                      emit('consentChange', purpose.value, channel.value, value)
                  "
                />
              </div>
            </div>

            <div class="mt-4 flex flex-wrap justify-end gap-2">
              <button
                v-for="channel in NOTIFICATION_CHANNELS"
                :key="`${purpose.value}-${channel.value}-all`"
                type="button"
                class="inline-flex items-center gap-2 rounded-full bg-[#f5eef9] px-3 py-1.5 text-[12px] font-semibold text-lais-primary"
                :disabled="isSavingConsents"
                @click="
                  emit('applyAllProfiles', purpose.value, channel.value, true)
                "
              >
                Aplicar {{ channel.label }} para todos os produtos
              </button>
            </div>
          </article>
        </div>
      </div>
    </div>
  </LaisCard>
</template>
